import React, { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  TextField,
} from '@material-ui/core';
import { PostProductBody, PostProductResponse } from 'src/interfaces/api/Product';
import LegacyProductCategorySelect from 'src/components/ApiField/LegacyProductCategorySelect';
import LegacyProductSupplierSelect from 'src/components/ApiField/LegacyProductSupplierSelect';
import api from 'src/config/api';
import { getJsonResponse } from 'src/utils/ApiUtils';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  open: boolean;
  productType: 'Hardware' | 'Virtual';
  onClose: () => void;
  onValidate: (type: 'Hardware' | 'Virtual', product: PostProductResponse) => void;
}

const NewProductModal: FC<Props> = ({ open, productType, onClose, onValidate }) => {
  const [formError, setFormError] = useState<string>();
  const defaultValues = useMemo(
    () => ({
      wording: '',
      commercialWording: '',
      description: '',
      category: undefined,
      accessCharges: 0,
      mensualRecurrency: 0,
      annualRecurrency: 0,
      supplier: undefined,
    }),
    []
  );
  const { fetch } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<PostProductBody>({
    defaultValues,
  });

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open]);

  const onSubmit = async (data: PostProductBody) => {
    const {
      description,
      wording,
      commercialWording,
      category,
      supplier,
      mensualRecurrency,
      annualRecurrency,
      accessCharges,
    } = data;
    const { endpoint, ...options } = api.legacyProduct.create(
      productType,
      wording,
      commercialWording,
      description,
      supplier,
      category,
      mensualRecurrency,
      annualRecurrency,
      accessCharges
    );

    setFormError(undefined);

    try {
      const response = await fetch(endpoint, options);

      const json = await getJsonResponse<PostProductResponse>(response);
      onValidate(productType, json);
    } catch (e) {
      setFormError(e.message || "Une erreur s'est produite");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} scroll='paper'>
      <DialogTitle>Créer un {productType === 'Hardware' ? 'produit' : 'service'}</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='wording'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  variant='outlined'
                  label='Label'
                  fullWidth
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='commercialWording'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant='outlined'
                  label='Label commercial'
                  fullWidth
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='description'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant='outlined'
                  label='Description'
                  multiline
                  minRows={3}
                  fullWidth
                  required
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='category'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <LegacyProductCategorySelect
                  {...field}
                  fullWidth
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='accessCharges'
              control={control}
              rules={{
                required: { value: true, message: 'Le champ est requis' },
                min: { value: 1, message: 'Le champ est requis' },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant='outlined'
                  label="Frais d'accès"
                  fullWidth
                  required
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name='mensualRecurrency'
              control={control}
              rules={{
                min: { value: 0, message: 'Le champ est requis' },
                required: { value: true, message: 'Le champ est requis' },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant='outlined'
                  label='Récurrence mensuelle'
                  fullWidth
                  required
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name='annualRecurrency'
              control={control}
              rules={{
                min: { value: 0, message: 'Le champ est requis' },
                required: { value: true, message: 'Le champ est requis' },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant='outlined'
                  label='Récurrence annuelle'
                  fullWidth
                  required
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='supplier'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <LegacyProductSupplierSelect
                  {...field}
                  fullWidth
                  type={productType}
                  error={undefined !== error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {formError && <FormHelperText error>{formError}</FormHelperText>}
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewProductModal;
